import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {
    Box,
    Typography,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Button,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Badge,
    Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    ArrowBack as BackIcon,
    Clear as ClearIcon,
    FilterListOff as FilterListOffIcon
} from '@mui/icons-material';
import {createApiClient} from '../../services/ApiClient';

const AuditAnalytics = ({accessToken, onBack, standalone = false}) => {
    const navigate = useNavigate();
    // State management
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalLogs, setTotalLogs] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [eventTypeFilter, setEventTypeFilter] = useState('');
    const [entityTypeFilter, setEntityTypeFilter] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');

    // Expanded row state for viewing details
    const [expandedLog, setExpandedLog] = useState(null);

    // Statistics
    const [statistics, setStatistics] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Create API client
    const apiClient = useMemo(() => {
        return accessToken ? createApiClient(accessToken) : null;
    }, [accessToken]);

    // Check if any server-side filters are active
    const hasActiveFilters = useMemo(() => {
        return eventTypeFilter || entityTypeFilter || userIdFilter;
    }, [eventTypeFilter, entityTypeFilter, userIdFilter]);

    // Fetch audit logs
    const fetchAuditLogs = async (pageNum = 0, pageSize = rowsPerPage, showLoading = true) => {
        if (!apiClient) return;

        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            const params = {
                limit: pageSize,
                offset: pageNum * pageSize
            };

            // Add filters if they exist
            if (eventTypeFilter) params.event_type = eventTypeFilter;
            if (entityTypeFilter) params.entity_type = entityTypeFilter;
            if (userIdFilter) params.user_id = userIdFilter;

            const logs = await apiClient.get('/api/audit/logs', params);
            setAuditLogs(logs);

            // In a real implementation, the API should return total count
            // For now, we estimate based on returned results
            if (logs.length < pageSize) {
                setTotalLogs((pageNum * pageSize) + logs.length);
            } else {
                setTotalLogs((pageNum + 1) * pageSize + 1);
            }

        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
            setError(error.message || 'Failed to load audit logs');
            setAuditLogs([]);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        if (!apiClient) return;

        try {
            setStatsLoading(true);
            const stats = await apiClient.get('/api/audit/stats');
            setStatistics(stats);
        } catch (error) {
            console.error('Failed to fetch audit statistics:', error);
            // Don't show error for stats, just log it
        } finally {
            setStatsLoading(false);
        }
    };

    // Initial data load - only once when apiClient is available
    useEffect(() => {
        if (apiClient && !initialLoadCompleted) {
            Promise.all([
                fetchAuditLogs(),
                fetchStatistics()
            ]).then(() => {
                setInitialLoadCompleted(true);
            }).catch((error) => {
                console.error('Initial data load failed:', error);
                setInitialLoadCompleted(true); // Set to true anyway to prevent infinite loading
            });
        }
    }, [apiClient]);

    // Handle filter changes - only after initial load is complete
    useEffect(() => {
        if (apiClient && initialLoadCompleted) {
            setPage(0);
            fetchAuditLogs(0, rowsPerPage);
        }
    }, [eventTypeFilter, entityTypeFilter, userIdFilter, initialLoadCompleted]);

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchAuditLogs(newPage, rowsPerPage, false);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchAuditLogs(0, newRowsPerPage);
    };

    // Handle search (client-side filtering)
    const handleSearch = () => {
        // Search is handled by filteredLogs memo
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setEventTypeFilter('');
        setEntityTypeFilter('');
        setUserIdFilter('');
        setPage(0);
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        setPage(0);
        await Promise.all([
            fetchAuditLogs(0, rowsPerPage),
            fetchStatistics()
        ]);
        setRefreshing(false);
    };

    // Filter logs by search term (client-side for text search)
    const filteredLogs = useMemo(() => {
        if (!searchTerm) {
            return auditLogs;
        }

        const term = searchTerm.toLowerCase();
        return auditLogs.filter(log =>
            log.event_type?.toLowerCase().includes(term) ||
            log.entity_type?.toLowerCase().includes(term) ||
            log.entity_id?.toLowerCase().includes(term) ||
            log.user_id?.toLowerCase().includes(term) ||
            log.ip_address?.toLowerCase().includes(term) ||
            log.endpoint?.toLowerCase().includes(term)
        );
    }, [auditLogs, searchTerm]);

    // Get unique values for filter dropdowns from statistics
    const uniqueEventTypes = useMemo(() => {
        if (statistics?.event_types) {
            return Object.keys(statistics.event_types).sort();
        }
        // Fallback to current logs if statistics not available
        const types = [...new Set(auditLogs.map(log => log.event_type).filter(Boolean))];
        return types.sort();
    }, [statistics, auditLogs]);

    const uniqueEntityTypes = useMemo(() => {
        if (statistics?.entity_types) {
            return Object.keys(statistics.entity_types).sort();
        }
        // Fallback to current logs if statistics not available
        const types = [...new Set(auditLogs.map(log => log.entity_type).filter(Boolean))];
        return types.sort();
    }, [statistics, auditLogs]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        try {
            return new Date(timestamp).toLocaleString();
        } catch {
            return timestamp;
        }
    };

    // Get event type color
    const getEventTypeColor = (eventType) => {
        if (eventType?.includes('created')) return 'success';
        if (eventType?.includes('updated')) return 'warning';
        if (eventType?.includes('deleted')) return 'error';
        if (eventType?.includes('login')) return 'info';
        return 'default';
    };

    // Render JSON viewer
    const renderJsonViewer = (data, isOldValue = false) => {
        if (!data) return 'N/A';

        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            return (
                <pre style={{
                    fontSize: '12px',
                    backgroundColor: isOldValue ? '#fef2f2' : '#f0fdf4',
                    padding: '12px',
                    borderRadius: '8px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    whiteSpace: 'pre-wrap',
                    border: `1px solid ${isOldValue ? '#fecaca' : '#bbf7d0'}`,
                    margin: 0,
                    fontFamily: 'Monaco, Consolas, "Roboto Mono", monospace'
                }}>
                    {JSON.stringify(parsed, null, 2)}
                </pre>
            );
        } catch {
            return String(data);
        }
    };

    // Handle back navigation
    const handleBackClick = () => {
        if (standalone) {
            navigate('/admin/dashboard');
        } else if (onBack) {
            onBack();
        }
    };

    if (error) {
        return (
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <Container maxWidth="xl" sx={{pt: 4, pb: 2}}>
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                    <Button onClick={handleBackClick} startIcon={<BackIcon/>}>
                        Back to Dashboard
                    </Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '87vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Container maxWidth="xl" sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                pt: 4,
                pb: 2,
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box
                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <IconButton onClick={handleBackClick}>
                            <BackIcon/>
                        </IconButton>
                        <Typography variant="h4" component="h1">
                            Audit Logs
                        </Typography>
                        {hasActiveFilters && (
                            <Chip
                                label="Filtered"
                                color="primary"
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={16}/> : <RefreshIcon/>}
                        onClick={handleRefresh}
                        disabled={loading || refreshing}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Filters */}
                <Paper sx={{p: 3, mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography variant="h6">
                            <FilterIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                            Filters
                        </Typography>
                        {(hasActiveFilters || searchTerm) && (
                            <Button
                                size="small"
                                startIcon={<FilterListOffIcon/>}
                                onClick={handleClearFilters}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{xs: 12, md: 3}}>
                            <TextField
                                fullWidth
                                label="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: searchTerm && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 3}}>
                            <FormControl fullWidth>
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventTypeFilter}
                                    label="Event Type"
                                    onChange={(e) => setEventTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    {uniqueEventTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                            {statistics?.event_types?.[type] && (
                                                <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                                                    ({statistics.event_types[type]})
                                                </Typography>
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12, md: 3}}>
                            <FormControl fullWidth>
                                <InputLabel>Entity Type</InputLabel>
                                <Select
                                    value={entityTypeFilter}
                                    label="Entity Type"
                                    onChange={(e) => setEntityTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Entities</MenuItem>
                                    {uniqueEntityTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                            {statistics?.entity_types?.[type] && (
                                                <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                                                    ({statistics.entity_types[type]})
                                                </Typography>
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12, md: 3}}>
                            <TextField
                                fullWidth
                                label="User ID"
                                value={userIdFilter}
                                onChange={(e) => setUserIdFilter(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {hasActiveFilters && (
                        <Box sx={{mt: 2, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Active server-side filters:
                            </Typography>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                {eventTypeFilter && (
                                    <Chip
                                        label={`Event: ${eventTypeFilter}`}
                                        onDelete={() => setEventTypeFilter('')}
                                        size="small"
                                    />
                                )}
                                {entityTypeFilter && (
                                    <Chip
                                        label={`Entity: ${entityTypeFilter}`}
                                        onDelete={() => setEntityTypeFilter('')}
                                        size="small"
                                    />
                                )}
                                {userIdFilter && (
                                    <Chip
                                        label={`User: ${userIdFilter}`}
                                        onDelete={() => setUserIdFilter('')}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Results Info */}
                <Box
                    sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0}}>
                    <Typography variant="body2" color="text.secondary">
                        {loading ? 'Loading...' : (
                            `Showing ${filteredLogs.length} of ${auditLogs.length} logs on this page (Total estimated: ${totalLogs})`
                        )}
                        {searchTerm && ` (filtered by "${searchTerm}")`}
                        {hasActiveFilters && ` [Server filters active]`}
                    </Typography>
                    {auditLogs.length > 0 && searchTerm && filteredLogs.length !== auditLogs.length && (
                        <Typography variant="body2" color="warning.main">
                            Client-side search active - showing {filteredLogs.length} of {auditLogs.length} loaded logs
                        </Typography>
                    )}
                </Box>

                {/* Audit Logs Table - Scrollable Container */}
                <Paper sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <TableContainer sx={{
                        flex: 1,
                        overflow: 'auto'
                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Entity</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>IP Address</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                            <CircularProgress/>
                                            <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                Loading audit logs...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredLogs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                            <Typography variant="body1" color="text.secondary">
                                                {auditLogs.length === 0 ? 'No audit logs found' : 'No logs match your search criteria'}
                                            </Typography>
                                            {hasActiveFilters && (
                                                <Button
                                                    size="small"
                                                    onClick={handleClearFilters}
                                                    sx={{mt: 1}}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredLogs.map((log) => (
                                        <React.Fragment key={log.id}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatTimestamp(log.timestamp)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={log.event_type}
                                                        color={getEventTypeColor(log.event_type)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {log.entity_type}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {log.entity_id}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {log.user_id ? (
                                                        <Typography variant="body2">
                                                            {log.user_id}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            System
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {log.ip_address || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setExpandedLog(
                                                                expandedLog === log.id ? null : log.id
                                                            )}
                                                        >
                                                            <ViewIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>

                                            {/* Expanded Details */}
                                            {expandedLog === log.id && (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{bgcolor: 'grey.50'}}>
                                                        <Box sx={{p: 2}}>
                                                            <Typography variant="subtitle1" gutterBottom>
                                                                Audit Log Details
                                                            </Typography>
                                                            <Divider sx={{mb: 2}}/>

                                                            {/* Request Information */}
                                                            <Box sx={{mb: 3}}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Request Information
                                                                </Typography>
                                                                <Box sx={{bgcolor: 'white', p: 2, borderRadius: 1}}>
                                                                    <Typography variant="body2" component="div" sx={{mb: 1}}>
                                                                        <strong>Endpoint:</strong> {log.endpoint || 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="body2" component="div" sx={{mb: 1}}>
                                                                        <strong>User Agent:</strong> {log.user_agent || 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="body2" component="div">
                                                                        <strong>Timestamp:</strong> {formatTimestamp(log.timestamp)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>

                                                            {/* Value Changes */}
                                                            {(log.old_values || log.new_values) && (
                                                                <Box>
                                                                    <Typography variant="subtitle2" gutterBottom sx={{mb: 2}}>
                                                                        Value Changes
                                                                    </Typography>
                                                                    <Grid container spacing={2}>
                                                                        {log.old_values && (
                                                                            <Grid size={{xs: 12, md: 6}}>
                                                                                <Typography variant="subtitle2" gutterBottom 
                                                                                    sx={{color: 'error.main', fontWeight: 'bold'}}>
                                                                                    Previous Values
                                                                                </Typography>
                                                                                {renderJsonViewer(log.old_values, true)}
                                                                            </Grid>
                                                                        )}

                                                                        {log.new_values && (
                                                                            <Grid size={{xs: 12, md: 6}}>
                                                                                <Typography variant="subtitle2" gutterBottom 
                                                                                    sx={{color: 'success.main', fontWeight: 'bold'}}>
                                                                                    New Values
                                                                                </Typography>
                                                                                {renderJsonViewer(log.new_values, false)}
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={totalLogs}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{flexShrink: 0}}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default AuditAnalytics;